
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  IconButton,
  Drawer,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DiamondIcon from '@mui/icons-material/Diamond';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import { useLocation, useNavigate } from 'react-router';
import MicIcon from '@mui/icons-material/Mic';
import AiSidebar from './AiSidebar.tsx';
import BrandPreferencesContent from './BrandPreferencesContent.tsx';
import CompanySetupPage from './CompanySetupPage.tsx';
import SourceFileUpload, { FileWithMetadata } from './SourceFileUpload.tsx';
import ModalForms from '../Shared/Modals/index.js';
import { AiActions, ConstantActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
// import BeautifyText from '../AiChatbot/BeautifyText.tsx';
import ReactMarkdown from 'react-markdown';
import { useWebSocketAiContext } from '../../utils/WebSocketContext.tsx';
import PresentationViewer from './PresentationViewer.tsx';
import CommonChatBubble from '../FlowBuilder/Common/ChatMessage/Commonchatbubble.tsx';
import { globalpermissions } from '../../Services/Permission.js';
import AiChipSelector from './AiChipSelector.tsx';
import BeautifyMarkdown from '../AiChatbot/Beautifymarkdown.tsx';

// ── Constants ─────────────────────────────────────────────────────────────────

const MODE_TABS = [
  { label: 'Query', icon: <ChatIcon sx={{ fontSize: 14 }} /> },
  { label: 'Image', icon: <ImageIcon sx={{ fontSize: 14 }} /> },
  { label: 'Presentation', icon: <SlideshowIcon sx={{ fontSize: 14 }} /> },
  { label: 'Research', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
  { label: 'Video Script', icon: <SmartToyIcon sx={{ fontSize: 14 }} /> },
];

const TAB_TYPES = ['chat', 'image', 'presentation', 'research', 'video'] as const;
type TabType = typeof TAB_TYPES[number];

const INSTRUCTION_KEYS: Record<number, keyof InstructionSet> = {
  0: 'chat_instruction',
  1: 'image_instruction',
  2: 'presentation_instruction',
  3: 'research_instruction',
  4: 'video_instruction',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Slide {
  title: string;
  layout: 'title' | 'content' | 'two_column' | 'closing';
  bullets: string[];
  left_title: string;
  left_bullets: string[];
  right_title: string;
  right_bullets: string[];
  speaker_notes: string;
}

interface Presentation {
  title: string;
  subtitle: string;
  slides: Slide[];
  filename?: string;
  pptUrl?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  imageUrl?: string;
  imageUrls?: string[];
  attachedDocs?: { name: string; type: string }[];
  presentation?: Presentation | null;
  pptUrl?: string;
  pptFilename?: string;
}

interface InstructionSet {
  chat_instruction?: string;
  image_instruction?: string;
  presentation_instruction?: string;
  research_instruction?: string;
  video_instruction?: string;
}

interface Project {
  id: string;
  name: string;
  emoji: string;
  chatCount: number;
  description?: string;
  category?: string;
  subCategory?: string;
}

interface Selection {
  type: string | null;
  category: string | null;
  sub: string | null;
  focus: string | null;
  format: string | null;
}

interface CompanyDocument {
  id: string;
  name: string;
  description: string;
  category: string;
  subCategory: string;
  sector: string;
  subSector: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
  isReference: boolean;
  createdDate?: string;
  relevance?: string;
  clientName?: string;
  status?: 'uploading' | 'uploaded' | 'error';
  uploadProgress?: number;
}

interface BrandPreferences {
  color_primary: string;
  color_accent: string;
  heading_font: string;
  body_font: string;
  title_size: number;
  body_size: number;
  slide_style: string;
  tone: string;
  logo_url?: string;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const TypingIndicator = () => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 2 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: '10px',
        bgcolor: 'primary.main', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 16 }} />
    </Box>
    <Box
      sx={{
        px: 2, py: 1.4, bgcolor: '#fff',
        border: '1px solid #ebebf0',
        borderRadius: '4px 16px 16px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex', gap: 0.5, alignItems: 'center',
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7, height: 7, borderRadius: '50%',
            bgcolor: 'primary.main', opacity: 0.5,
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '40%': { transform: 'scale(1.4)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

const FileChip: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const isImage = file.type.startsWith('image/');
  const ext = file.name.split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE';

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.8,
        bgcolor: '#f5f5f9', border: '1px solid #e8e8f0',
        borderRadius: '8px', px: 1, py: 0.5,
      }}
    >
      {isImage && preview ? (
        <Box
          component="img" src={preview}
          sx={{ width: 28, height: 28, borderRadius: '5px', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 28, height: 28, bgcolor: '#e8eaff', borderRadius: '5px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, color: 'primary.main', flexShrink: 0,
          }}
        >
          {ext}
        </Box>
      )}
      <Typography sx={{ fontSize: 12, maxWidth: 100, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {file.name}
      </Typography>
      <IconButton size="small" onClick={onRemove} sx={{ p: 0.2, color: '#bbb', '&:hover': { color: '#555' } }}>
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const Ai = () => {
  const theme = useTheme();
  const action = AiActions();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackBar } = useSnackBar();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const constActions = ConstantActions();
  const { state: wsState } = useWebSocketAiContext();

  // ── Refs ──
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<HTMLDivElement>(null);

  // ── User info ──
  const userInfo = localStorage?.getItem('userInfo');
  const parsedUser = useMemo(() => (userInfo ? JSON.parse(userInfo) : {}), [userInfo]);
  const ORGANIZATION_ID = parsedUser?.profile?.organisation?.organisation_id ?? null;
  const companyName = parsedUser?.profile?.organisation?.name || parsedUser?.first_name || location.state?.companyName || 'Your Company';

  // ── UI state ──
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const recognitionRef = useRef<any>(null);
  const [isListening, setIsListening] = useState(false);
  // ── Company setup state ──
  const [companyDocuments, setCompanyDocuments] = useState<CompanyDocument[]>([]);
  const [brandPreferences, setBrandPreferences] = useState<BrandPreferences>({
    color_primary: '#4962E5',
    color_accent: '#7C3AED',
    heading_font: 'Poppins',
    body_font: 'Inter',
    title_size: 42,
    body_size: 16,
    slide_style: 'Modern',
    tone: 'Professional',
    logo_url: '',
  });
  const [isSetupComplete, setIsSetupComplete] = useState(false);

  // ── Project source files state ──
  const [sourceFiles, setSourceFiles] = useState<FileWithMetadata[]>([]);

  // ── Chat state ──
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  // ── Conversation / project state ──
  const [conversationList, setConversationList] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // ── Misc ──
  const [instructionSet, setInstructionSet] = useState<InstructionSet>({});
  const [selectedCampaign, setSelectedCampaign] = useState<any>({});
  const [leadFormId, setLeadFormId] = useState<number | null>(null);
  const [lastChipSelection, setLastChipSelection] = useState<Selection | null>(null);

  // ── Derived ──
  const hasChatStarted = messages.length > 0;
  const currentType: TabType = TAB_TYPES[activeTab] ?? 'chat';
  const currentInstruction = instructionSet[INSTRUCTION_KEYS[activeTab]] ?? '';
  const selectedProject = projectList.find(p => p.id === selectedProjectId);
  const hasSourceFiles = sourceFiles.length > 0;
  const isCampaignSelected = !!(
    selectedCampaign &&
    Object.keys(selectedCampaign).length > 0 &&
    (selectedCampaign.proposal_id || selectedCampaign.account)
  );
  // ── Breadcrumbs ──
  const breadcrumbItems = useMemo(() => {
    if (!lastChipSelection) return [];
    const stepKeys: (keyof Selection)[] = ['type', 'category', 'sub', 'focus', 'format'];
    return stepKeys
      .filter((key) => lastChipSelection[key])
      .map((key, idx, arr) => ({
        label: lastChipSelection[key] as string,
        onClick: () => {
          const next = { ...lastChipSelection };
          arr.forEach((k, i) => { if (i >= idx) next[k] = null; });
          const isEmpty = arr.every((k) => next[k] === null);
          setLastChipSelection(isEmpty ? null : next);
          setMessage('');
        },
      }));
  }, [lastChipSelection]);

  // ── Data fetchers ──
  const fetchConversations = async () => {
    try {
      const res = await action.getConversationList({ parameters: { dashboard: 'machadalo_studio' } });
      setConversationList(res?.data?.results || res?.data || []);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await action.getProjectList({ organisation_id: 'MAC1421', campaign_id: 'MACTES8BF9' });
      const list = res?.data?.results || res?.data || [];
      setProjectList(list.map((p: any) => ({
        id: p.project_id || p._id,
        name: p.project_name || p.name,
        emoji: p.emoji || '📁',
        chatCount: p.chat_count || p.chatCount || 0,
        description: p.description || '',
        category: p.category || '',
        subCategory: p.sub_category || p.subCategory || '',
      })));
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  // ── Handlers ──
  const handleNewConversation = () => {
    setCurrentConversationId(null);
    setSelectedConversationId(null);
    setActiveTab(0);
    setMessages([]);
    setAttachedFiles([]);
    setMessage('');
  };

  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
    setCurrentConversationId(id);
    setMessage('');
    setAttachedFiles([]);
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleOpenInstructionSet = () => {
    handleSettingsClose();
    navigate('/r/machadalostudio/instructionsetform');
  };

  const handleOpenBrandPreferences = () => {
    navigate('/r/machadalostudio/companysetup/brand/');
    // handleSettingsClose();
    // setBrandModalOpen(true);
  };

  const handleOpenCompanySetup = () => {
    handleSettingsClose();
    navigate('/r/machadalostudio/companysetup')
    // setIsSetupComplete(false);
  };

  const handleCreateProject = async (name: string, description?: string, metadata?: { category?: string; subCategory?: string; projectType?: 'create' | 'update' }
  ) => {
    try {
      const res = await action.createProject({
        project_name: name,
        description: description || '',
        organisation_id: ORGANIZATION_ID,
        campaign_id: 'MACTES8BF9',
        category: metadata?.category || '',
        sub_category: metadata?.subCategory || '',
      });
      if (res?.status === 200 || res?.status === 201) {
        await fetchProjects();
        const newId = res?.data?.project_id || res?.data?.id || res?.data?._id;
        setSelectedProjectId(newId);
        showSnackBar('Project created successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      showSnackBar('Failed to create project. Please try again.', 'error');
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setLastChipSelection(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuery();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setAttachedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...newFiles.filter((f) => !existing.has(f.name + f.size))];
    });
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Source file handlers ──
  const handleSourceFilesAdded = (files: FileWithMetadata[]) => {
    setSourceFiles(prev => [...prev, ...files]);
    showSnackBar(`Added ${files.length} file(s) to project`, 'success');
  };

  const handleRemoveSourceFile = (id: string) => {
    setSourceFiles(prev => prev.filter(f => f.id !== id));
  };

  const toCommonMsg = (msg: ChatMessage) => ({
    id: msg.id,
    type: (msg.role === 'user' ? 'user' : 'ai') as 'user' | 'ai',
    content: msg.text,
    created_at: msg.timestamp?.toISOString(),
    presentation: msg.presentation,
    image_url: msg.imageUrls,
    attachedDocs: msg.attachedDocs,
  });

  // ── Submit query ──
  const submitQuery = async () => {
    const trimmed = message.trim();
    if ((!trimmed && attachedFiles.length === 0) || isLoading) return;

    const imageFiles = attachedFiles.filter((f) => f.type.startsWith('image/'));
    const docFiles = attachedFiles.filter((f) => !f.type.startsWith('image/'));

    const previewUrls = imageFiles.map((f) => URL.createObjectURL(f));
    const attachedDocs = docFiles.map((f) => ({ name: f.name, type: f.type }));

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: trimmed,
      timestamp: new Date(),
      imageUrls: previewUrls.length > 0 ? previewUrls : undefined,
      attachedDocs: attachedDocs.length > 0 ? attachedDocs : undefined,
    };

    setMessages((prev) => [...prev, userMsg]);
    setMessage('');
    setIsLoading(true);

    try {
      if (currentType === 'presentation') {
        await action.organizationChat({
          organization_id: ORGANIZATION_ID,
          project_id: selectedProjectId || '',
          user_id: parsedUser?.id || '',
          lead_form_id: leadFormId ? String(leadFormId) : '',
          action: 'presentation',
          type: lastChipSelection?.type || '',
          query: trimmed,
          category: lastChipSelection?.category || '',
          sub_category: lastChipSelection?.sub || '',
          instruction_set: currentInstruction,
          format: 'pptx',
        });
        setAttachedFiles([]);
        return;
      }

      const parameters = {
        dashboard: 'machadalo_studio',
        user_id: parsedUser?.id || '',
      };

      const formData = new FormData();
      formData.append('organization_id', ORGANIZATION_ID);
      formData.append('project_id', selectedProjectId || '');
      formData.append('query', trimmed);
      formData.append('action', currentType);
      formData.append('instruction', currentInstruction);
      formData.append('parameters', JSON.stringify(parameters));

      if (companyDocuments.length > 0) {
        formData.append('company_documents', JSON.stringify(companyDocuments.map(d => ({
          name: d.name,
          category: d.category,
          sector: d.sector,
        }))));
      }

      if (brandPreferences && Object.keys(brandPreferences).length > 0) {
        formData.append('brand_preferences', JSON.stringify(brandPreferences));
      }

      if (sourceFiles.length > 0) {
        formData.append('project_sources', JSON.stringify(sourceFiles.map(s => ({
          name: s.name,
          description: s.description,
          category: s.category,
          subCategory: s.subCategory,
          size: s.size,
          type: s.type,
        }))));
      }

      if (leadFormId) formData.append('lead_form_id', String(leadFormId));
      if (selectedCampaign?.proposal_id) formData.append('campaign_id', selectedCampaign.proposal_id);

      if (lastChipSelection) {
        formData.append('type', lastChipSelection.type || '');
        formData.append('category', lastChipSelection.category || '');
        formData.append('sub_category', lastChipSelection.sub || '');
        formData.append('topic', lastChipSelection.focus || '');
        formData.append('format', lastChipSelection.format || '');
      }

      const urlMatch = trimmed.match(/https?:\/\/[^\s]+/);
      if (urlMatch) formData.append('url', urlMatch[0]);
      if (currentConversationId) formData.append('conversation_id', currentConversationId);
      attachedFiles.forEach((file) => formData.append('files', file));

      const res = await action.organizationChat(formData);
      const conversationId = res?.conversation_id || res?.data?.conversation_id;

      if (conversationId && !currentConversationId) {
        setCurrentConversationId(conversationId);
        fetchConversations();
      }

      if (currentType === 'image') {
        const imageUrl = res?.data?.image_url;
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: imageUrl ? '' : 'Sorry, could not generate image.',
          timestamp: new Date(),
          imageUrl,
        }]);
      } else {
        const aiText = res?.data?.response || res?.response?.response;
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: String(aiText || ''),
          timestamp: new Date(),
        }]);
      }

      setAttachedFiles([]);
    } catch (error) {
      console.error(error);
      showSnackBar('Failed to send message. Please try again.', 'error');
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setMessage(trimmed);
    } finally {
      if (currentType !== 'presentation') {
        setIsLoading(false);
      }
    }
  };

  // ── Handle company setup completion ──
  const handleSetupComplete = (data: {
    documents: CompanyDocument[];
    brandPreferences: BrandPreferences;
  }) => {
    setCompanyDocuments(data.documents);
    setBrandPreferences(data.brandPreferences);
    // setIsSetupComplete(true);
    showSnackBar('Company setup complete! You can now start chatting.', 'success');
  };

  const handleSkipSetup = () => {
    // setIsSetupComplete(true);
    showSnackBar('You can complete company setup later from the settings menu.', 'info');
  };

  // ── Effects ──
  useEffect(() => {
    fetchConversations();
    fetchProjects();

    const savedSetup = localStorage.getItem(`company_setup_${ORGANIZATION_ID}`);
    if (savedSetup) {
      try {
        const data = JSON.parse(savedSetup);
        setCompanyDocuments(data.documents || []);
        if (data.brandPreferences) {
          setBrandPreferences(data.brandPreferences);
        }
        // setIsSetupComplete(data.isComplete || false);
      } catch (e) {
        console.error('Failed to load saved setup:', e);
      }
    }
  }, []);

  const stopMic = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const handleMicClick = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SR) {
      showSnackBar('Speech recognition not supported in this browser.', 'error');
      return;
    }

    if (isListening) {
      stopMic();
      return;
    }

    const rec = new SR();
    recognitionRef.current = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event: any) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        }
      }
      if (finalTranscript) {
        setMessage((prev) => prev + finalTranscript);
      }
    };

    rec.onend = () => setIsListening(false);
    rec.onerror = () => setIsListening(false);

    rec.start();
    setIsListening(true);
  }, [isListening, stopMic, showSnackBar]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (ORGANIZATION_ID) {
      localStorage.setItem(`company_setup_${ORGANIZATION_ID}`, JSON.stringify({
        documents: companyDocuments,
        brandPreferences,
        isComplete: isSetupComplete,
      }));
    }
  }, [companyDocuments, brandPreferences, isSetupComplete, ORGANIZATION_ID]);

  useEffect(() => {
    if (ORGANIZATION_ID && selectedProjectId) {
      localStorage.setItem(`project_sources_${ORGANIZATION_ID}_${selectedProjectId}`, JSON.stringify(sourceFiles));
    }
  }, [sourceFiles, ORGANIZATION_ID, selectedProjectId]);

  useEffect(() => {
    if (ORGANIZATION_ID && selectedProjectId) {
      const savedSources = localStorage.getItem(`project_sources_${ORGANIZATION_ID}_${selectedProjectId}`);
      if (savedSources) {
        try {
          setSourceFiles(JSON.parse(savedSources));
        } catch (e) {
          console.error('Failed to load project sources:', e);
        }
      }
    }
  }, [ORGANIZATION_ID, selectedProjectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!ORGANIZATION_ID) return;
    (async () => {
      try {
        const res = await action.getOrganizationInstruction(ORGANIZATION_ID);
        const record = Array.isArray(res?.data) ? res.data[0] : res?.data?.[0] || res?.[0] || res;
        if (record?.instruction_set) setInstructionSet(record.instruction_set);
      } catch (err) {
        console.error('Fetch error:', err);
      }
    })();
  }, [ORGANIZATION_ID]);

  useEffect(() => {
    if (!selectedConversationId) return;
    (async () => {
      setIsLoading(true);
      setMessages([]);
      try {
        const history = await action.getChatHistory({
          parameters: { dashboard: 'machadalo_studio' },
          ai_model: 'mdai',
          conversation_id: selectedConversationId,
        });

        if (history?.status === 200 && Array.isArray(history?.data)) {
          const loaded: ChatMessage[] = [];
          history.data.forEach((item: any) => {
            if (item.query) {
              loaded.push({
                id: `${item._id}-user`,
                role: 'user',
                text: item.query,
                timestamp: new Date(item.created_at || Date.now()),
              });
            }
            const response = item?.response;
            if (!response) return;

            const presentation = response?.presentation;
            if (presentation) {
              loaded.push({
                id: `${item._id}-ai`,
                role: 'assistant',
                text: '__PRESENTATION__',
                timestamp: new Date(item.updated_at || item.created_at || Date.now()),
                presentation: {
                  title: presentation.title,
                  subtitle: presentation.subtitle,
                  slides: presentation.slides,
                  filename: response?.filename,
                },
                pptUrl: response?.file_url,
                pptFilename: response?.filename,
              });
            } else if (response?.response) {
              loaded.push({
                id: `${item._id}-ai`,
                role: 'assistant',
                text: response.response,
                timestamp: new Date(item.updated_at || item.created_at || Date.now()),
              });
            }
          });
          setMessages(loaded);
        }
      } catch (err) {
        console.error('Failed to load history:', err);
        showSnackBar('Could not load conversation history.', 'error');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedConversationId]);

  useEffect(() => {
    if (!wsState?.aiRes) return;
    const aiResponse = wsState.aiRes;

    if (aiResponse?.success === false) {
      const raw = aiResponse?.error || 'Something went wrong.';
      const isQuotaError = raw.includes('insufficient_quota') || raw.includes('429');
      const msg = isQuotaError
        ? 'AI quota exceeded. Please check your plan or billing.'
        : raw.length > 100 ? raw.slice(0, 100) + '…' : raw;
      showSnackBar(msg, 'error');
      setIsLoading(false);
      return;
    }

    const presentation = aiResponse?.ai_response?.presentation;
    showSnackBar('Generating your presentation, this may take a few seconds… ⏳', 'info');

    if (presentation) {
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: '__PRESENTATION__',
        timestamp: new Date(),
        presentation: {
          title: presentation.title,
          subtitle: presentation.subtitle,
          slides: presentation.slides,
          pptUrl: aiResponse?.file_url,
          filename: aiResponse?.filename,
        },
        pptUrl: aiResponse?.file_url,
        pptFilename: aiResponse?.filename,
      }]);
      setIsLoading(false);
    } else if (aiResponse?.response) {
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: aiResponse.response,
        timestamp: new Date(),
      }]);
      setIsLoading(false);
    }

    if (aiResponse?.image_url) {
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: '',
        timestamp: new Date(),
        imageUrl: aiResponse.image_url,
      }]);
      setIsLoading(false);
    }
  }, [wsState?.aiRes]);

  // ── Sidebar shared props ──
  const sharedSidebarProps = {
    isMobile,
    navigate,
    activeTab,
    onTabChange: setActiveTab,
    companyName,
    onNewConversation: handleNewConversation,
    conversationList,
    onSelectConversation: handleSelectConversation,
    selectedConversationId,
    projectList,
    selectedProjectId,
    isCampaignSelected,
    onSelectProject: setSelectedProjectId,
    onCreateProject: handleCreateProject,
    onCampaignSelected: (val: any) => setSelectedCampaign(val),
    onLeadFormFetched: (id: number) => setLeadFormId(id),
    // onResetChips: () => setLastChipSelection(null),
    onResetChips: () => {
      setLastChipSelection(null);
      setAttachedFiles([]);
      setCurrentConversationId(null);
      setSelectedConversationId(null);
      setActiveTab(0);
      setMessages([]);
    },
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 0.5,
        height: { xs: 'calc(100vh - 30px)', sm: 'calc(100vh - 30px)' },
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1, md: 'auto' },
      }}
    >
      <Grid container spacing={0.25} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* ── Mobile Sidebar Drawer ── */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: '85%', maxWidth: 360 } }}
          >
            <AiSidebar {...sharedSidebarProps} onClose={() => setSidebarOpen(false)} />
          </Drawer>
        )}

        {/* ── Desktop Sidebar ── */}
        {!isMobile && (
          <Grid item md={3} sx={{ height: '100%' }}>
            <Paper elevation={2} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <AiSidebar {...sharedSidebarProps} onSelectProject={setSelectedProjectId} />
            </Paper>
          </Grid>
        )}

        {/* ── Main Chat Area ── */}
        <Grid item xs={12} md={isMobile ? 12 : 9} sx={{ height: '100%', display: 'flex' }}>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3, overflow: 'hidden', width: '100%',
              display: 'flex', flexDirection: 'column',
              mb: '5px', position: 'relative', bgcolor: '#f6f6f9',
            }}
          >
            {/* ── Top Bar ── */}
            <Box
              sx={{
                px: 2.5, py: 1.2, bgcolor: '#fff',
                borderBottom: '1px solid #ebebf0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#555', mr: 0.5 }}>
                    <MenuIcon />
                  </IconButton>
                )}
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', lineHeight: 1.2 }}>
                    Chat Studio
                  </Typography>
                  {breadcrumbItems.length > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMobile && breadcrumbItems.length > 1 ? (
                        <>
                          <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>...</Typography>
                          <Typography sx={{ fontSize: 12, color: '#1a1a2e', fontWeight: 500 }}>
                            {breadcrumbItems[breadcrumbItems.length - 1].label}
                          </Typography>
                        </>
                      ) : (
                        breadcrumbItems.map((item, idx) => (
                          <Typography
                            key={idx}
                            sx={{ fontSize: 12, color: idx === breadcrumbItems.length - 1 ? '#1a1a2e' : '#9e9e9e', cursor: 'pointer' }}
                            onClick={item.onClick}
                          >
                            {idx > 0 && ' / '}{item.label}
                          </Typography>
                        ))
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>
                      {companyName} · Ask anything
                    </Typography>
                  )}
                </Box>

                {/* ── Project Name Badge ── */}
                {selectedProjectId && selectedProject && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.2,
                      py: 0.3,
                      bgcolor: 'rgba(124,58,237,0.08)',
                      borderRadius: '6px',
                      border: '1px solid rgba(124,58,237,0.15)',
                    }}
                  >
                    <FolderIcon sx={{ fontSize: 14, color: '#7C3AED' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#7C3AED' }}>
                      {selectedProject.name}
                    </Typography>
                    {hasSourceFiles && (
                      <Chip
                        label={sourceFiles.length}
                        size="small"
                        sx={{ height: 16, fontSize: 8, bgcolor: 'rgba(124,58,237,0.1)', color: '#7C3AED' }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {!isSetupComplete && (
                  <Button
                    size="small"
                    startIcon={<BuildIcon sx={{ fontSize: 15 }} />}
                    onClick={handleOpenCompanySetup}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      color: '#7C3AED',
                      fontWeight: 500,
                      fontSize: 12,
                      border: '1px solid #7C3AED',
                      px: 1.5,
                      bgcolor: 'rgba(124,58,237,0.06)',
                      '&:hover': { bgcolor: 'rgba(124,58,237,0.12)' },
                    }}
                  >
                    Setup Company
                  </Button>
                )}

                {isSetupComplete && (
                  <Chip
                    label="✓ Ready"
                    size="small"
                    sx={{
                      bgcolor: '#dcfce7',
                      color: '#14532d',
                      fontWeight: 700,
                      fontSize: 11,
                      height: 24,
                    }}
                  />
                )}

                {/* {constActions.userHasPermission([globalpermissions?.machadaloStudioDashboard?.presentationBrand]) && (
                  <Button
                    startIcon={<DiamondIcon sx={{ fontSize: 15 }} />}
                    size="small"
                    onClick={() => setBrandModalOpen(true)}
                    sx={{
                      textTransform: 'none', border: '1px solid #e0e0ee',
                      borderRadius: '8px', color: '#444', fontWeight: 500, fontSize: 13,
                      px: 1.5, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    Brand
                  </Button>
                )} */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1, }}>
                  {brandPreferences?.logo_url ? (
                    <Box
                      component="img"
                      src={brandPreferences.logo_url}
                      alt={companyName}
                      sx={{ height: 22, width: 'auto', maxWidth: 100, objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: '#1a1a2e',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {companyName}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={handleSettingsClick}
                  sx={{
                    border: '1px solid #e0e0ee',
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <SettingsIcon fontSize="small" sx={{ color: '#666' }} />
                </IconButton>

                <Menu
                  anchorEl={settingsMenuAnchor}
                  open={Boolean(settingsMenuAnchor)}
                  onClose={handleSettingsClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      borderRadius: '12px',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      minWidth: 220,
                    },
                  }}
                >
                  {/* <MenuItem onClick={handleOpenCompanySetup}>
                    <ListItemIcon>
                      <BuildIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Company Setup</ListItemText>
                  </MenuItem> */}

                  <MenuItem onClick={handleOpenBrandPreferences}>
                    <ListItemIcon>
                      <DiamondIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Brand</ListItemText>
                  </MenuItem>

                  {/* <Divider /> */}

                  <MenuItem onClick={handleOpenInstructionSet}>
                    <ListItemIcon>
                      <AutoAwesomeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Instruction Set</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={() => {
                    handleSettingsClose();
                    navigate('/r/machadalostudio/history');
                  }}>
                    <ListItemIcon>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>History</ListItemText>
                  </MenuItem>

                  {/* <MenuItem onClick={() => {
                    handleSettingsClose();
                    navigate('/r/machadalostudio/avatar-create');
                  }}>
                    <ListItemIcon>
                      <FolderIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Avatar Creator</ListItemText>
                  </MenuItem> */}
                </Menu>
              </Box>
            </Box>

            {/* ── Content Area ── */}
            <Box
              ref={chatBoxRef}
              sx={{
                flex: 1, overflowY: 'auto',
                px: { xs: 2, md: 4 }, py: 2,
                bgcolor: '#f6f6f9',
                display: 'flex', flexDirection: 'column',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 3 },
              }}
            >
              {!hasChatStarted ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 900, mx: 'auto' }}>

                  {/* ── Company Setup ── */}
                  {/* {!isSetupComplete && (
                    <Box
                      id="company-setup-section"
                      sx={{
                        width: '100%',
                        maxWidth: 700,
                        mb: 2,
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        p: 2,
                      }}
                    >
                      <CompanySetupPage
                        companyId={ORGANIZATION_ID || 'new'}
                        companyName={companyName}
                        onComplete={handleSetupComplete}
                        onSkip={handleSkipSetup}
                        initialDocuments={companyDocuments}
                        initialBrand={brandPreferences}
                      />
                    </Box>
                  )} */}

                  {/* ── Welcome Header ── */}
                  {/* {isSetupComplete && ( */}
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: '#1a1a2e' }}>
                      How can I help{' '}
                      <Box component="span" sx={{ color: 'primary.main' }}>{companyName}</Box>{' '}
                      today?
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: '#9e9e9e', mt: 0.5 }}>
                      {selectedProjectId && selectedProject ? (
                        <>Working on <Box component="span" sx={{ fontWeight: 600, color: '#7C3AED' }}>"{selectedProject.name}"</Box></>
                      ) : (
                        'Pick a starter, browse by category, or describe what you need.'
                      )}
                    </Typography>
                  </Box>
                  {/* )} */}

                  {/* ── Source Files Section ── */}
                  {selectedProjectId && selectedProject && (
                    <Box sx={{ width: '100%', maxWidth: 680, mb: 2 }}>
                      <SourceFileUpload
                        onFilesAdded={handleSourceFilesAdded}
                        existingFiles={sourceFiles}
                        onRemove={handleRemoveSourceFile}
                      />
                    </Box>
                  )}

                  {/* ── Chip Selector ── */}
                  {/* {isSetupComplete && ( */}
                  <Box sx={{ width: '100%', maxWidth: 680 }}>
                    <AiChipSelector
                      mode={currentType}
                      value={lastChipSelection ?? { type: null, category: null, sub: null, focus: null, format: null }}
                      onSelect={(prompt, meta) => {
                        setMessage(prompt);
                        setLastChipSelection(meta);
                      }}
                      onChange={(meta) => setLastChipSelection(meta)}
                    />
                  </Box>
                  {/* )} */}
                </Box>
              ) : (
                /* ── Chat Messages ── */
                <Box sx={{ maxWidth: '100%', width: '100%', mx: 'auto' }}>
                  {messages.map((msg) => (
                    <CommonChatBubble
                      key={msg.id}
                      message={toCommonMsg(msg)}
                      aiAvatar={
                        <Box
                          sx={{
                            width: 32, height: 32, borderRadius: '10px',
                            bgcolor: 'primary.main', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 16 }} />
                        </Box>
                      }
                      userLabel={companyName?.[0]?.toUpperCase() || 'U'}
                      // renderAiContent={(content) => (
                      //   <Box
                      //     sx={{
                      //       '& .MuiTypography-body1': { fontSize: '13.5px !important', lineHeight: '1.7 !important', color: '#1a1a2e' },
                      //       '& p': { margin: 0 },
                      //     }}
                      //   >
                      //     <BeautifyText text={content} />
                      //   </Box>
                      // )}
                      renderAiContent={(content) => (
                        <Box
                          sx={{
                            fontSize: '13.5px',
                            lineHeight: 1.7,
                            color: '#1a1a2e',
                            '& p': { margin: 0, mb: 1 },
                          }}
                        >
                         <BeautifyMarkdown text={content} />
                        </Box>
                      )}
                      imageSlot={
                        (() => {
                          const hasImages = msg.imageUrls && msg.imageUrls.length > 0;
                          const hasDocs = msg.attachedDocs && msg.attachedDocs.length > 0;
                          const hasAiImage = Boolean(msg.imageUrl);

                          if (!hasImages && !hasDocs && !hasAiImage) return undefined;

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                              {hasAiImage && (
                                <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #e8e8f0', maxWidth: 420 }}>
                                  <Box component="img" src={msg.imageUrl} alt="Generated" sx={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                                </Box>
                              )}
                              {hasImages && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {msg.imageUrls!.map((url, i) => (
                                    <Box
                                      key={i}
                                      component="img"
                                      src={url}
                                      alt={`Image ${i + 1}`}
                                      sx={{
                                        height: 110,
                                        maxWidth: 180,
                                        borderRadius: '10px',
                                        objectFit: 'cover',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => window.open(url, '_blank')}
                                    />
                                  ))}
                                </Box>
                              )}
                              {hasDocs && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                  {msg.attachedDocs!.map((doc, i) => {
                                    const isPdf = doc.type === 'application/pdf';
                                    const ext = doc.name.split('.').pop()?.toUpperCase() || 'FILE';
                                    return (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.8,
                                          bgcolor: 'rgba(255,255,255,0.15)',
                                          border: '1px solid rgba(255,255,255,0.25)',
                                          borderRadius: '8px',
                                          px: 1,
                                          py: 0.6,
                                          backdropFilter: 'blur(4px)',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '6px',
                                            bgcolor: isPdf ? 'rgba(239,68,68,0.2)' : 'rgba(102,126,234,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            color: isPdf ? '#ef4444' : '#667eea',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {ext}
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            maxWidth: 120,
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            color: 'rgba(255,255,255,0.9)',
                                          }}
                                        >
                                          {doc.name}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        })()
                      }
                      presentationSlot={
                        msg.presentation ? (
                          <PresentationViewer
                            title={msg.presentation.title}
                            subtitle={msg.presentation.subtitle}
                            pptUrl={msg.presentation.pptUrl || msg.pptUrl}
                            pptFilename={msg.presentation.filename || msg.pptFilename}
                          />
                        ) : undefined
                      }
                      formatTime={(d) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      variant="studio"
                    />
                  ))}
                  {isLoading && <TypingIndicator />}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* ── Bottom Input Area ── */}
            {/* {isSetupComplete && ( */}
            <Box sx={{ px: 2.5, pb: 1.5, pt: 1, bgcolor: '#f6f6f9', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                {MODE_TABS.map((t, i) => (
                  <Chip
                    key={t.label}
                    label={t.label}
                    icon={React.cloneElement(t.icon, {
                      sx: { fontSize: 14, color: i === activeTab ? '#fff !important' : '#888 !important' },
                    })}
                    onClick={() => handleTabChange(i)}
                    size="small"
                    sx={{
                      borderRadius: '999px',
                      bgcolor: i === activeTab ? 'primary.main' : '#fff',
                      color: i === activeTab ? '#fff' : '#444',
                      border: `1px solid ${i === activeTab ? 'transparent' : '#e0e0ee'}`,
                      fontWeight: i === activeTab ? 700 : 500,
                      fontSize: 12,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  bgcolor: '#fff', border: '1px solid #e0e0ee',
                  borderRadius: '14px', overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                {attachedFiles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, pt: 1.2, pb: 0.5, borderBottom: '1px solid #f0f0f5' }}>
                    {attachedFiles.map((file, idx) => (
                      <FileChip key={idx} file={file} onRemove={() => removeFile(idx)} />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
                  <TextField
                    fullWidth multiline maxRows={4}
                    disabled={!isCampaignSelected}
                    placeholder={
                      isCampaignSelected
                        ? "Message Chat…  (Shift+Enter for newline)"
                        : "Please select campaign first"
                    }
                    // placeholder="Message Chat…  (Shift+Enter for newline)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="standard"
                    // InputProps={{ disableUnderline: true }}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              disabled={!isCampaignSelected}
                              onClick={handleMicClick}
                              sx={{ color: isListening ? 'error.main' : 'inherit' }}
                            >
                              <MicIcon fontSize="small" sx={isListening ? { animation: 'pulse 1s infinite' } : {}} />
                            </IconButton>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: 13.5,
                        color: '#2a2a3e',
                        '&::placeholder': {
                          color: isCampaignSelected ? '#bbb' : '#EF4444',
                          opacity: 1,
                        },
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: isCampaignSelected ? '#2a2a3e' : undefined,
                        '&::placeholder': {
                          color: !isCampaignSelected ? '#EF4444' : '#bbb',
                          opacity: 1,
                          WebkitTextFillColor: !isCampaignSelected ? '#EF4444' : undefined,
                        },
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ color: '#bbb', '&:hover': { color: '#666' } }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={(!message.trim() && attachedFiles.length === 0) || isLoading}
                    onClick={submitQuery}
                    sx={{
                      bgcolor: (message.trim() || attachedFiles.length > 0) && !isLoading ? 'primary.main' : '#e8e8f0',
                      color: (message.trim() || attachedFiles.length > 0) && !isLoading ? '#fff' : '#bbb',
                      borderRadius: '10px', width: 34, height: 34,
                      '&.Mui-disabled': { bgcolor: '#e8e8f0', color: '#bbb' },
                    }}
                  >
                    {isLoading ? <CircularProgress size={16} sx={{ color: '#bbb' }} /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Box>
            {/* )} */}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Brand Preferences Modal ── */}
      {/* <ModalForms
        show={brandModalOpen}
        handleClose={() => setBrandModalOpen(false)}
        modalHeader=""
        size="md"
        modalBody={
          <BrandPreferencesContent
            onCancel={() => setBrandModalOpen(false)}
            onSave={() => setBrandModalOpen(false)}
            onReset={() => { }}
            companyName={companyName}
          />
        }
      /> */}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </Container>
  );
};

export default Ai;